import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { EditMenuAPI } from '../redux/actions/Menu/EditMenuAction';
import { GetMenuApi } from '../redux/actions/Menu/GetListAction';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { GetFoodCategoriesDropdownAPI } from '../redux/actions/Content/GetFoodCategoryDropdown';

const MenuDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    foodCategory: '',
    day: '',
    images: []
  });
  const [newImages, setNewImages] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  const { loading, response, error } = useSelector(state => state.menuList || {});
  const { loading: updateLoading, response: updateResponse, error: updateError } =
    useSelector(state => state.editMenu || {});
  const { response: foodCategoriesResponse, loading: foodCategoriesLoading } = useSelector(state => state.getFoodCategoryDropdown || {});

  // Days of week for dropdown
  const daysOfWeek = [
    { value: '0', label: 'Monday' },
    { value: '1', label: 'Tuesday' },
    { value: '2', label: 'Wednesday' },
    { value: '3', label: 'Thursday' },
    { value: '4', label: 'Friday' },
    { value: '5', label: 'Saturday' },
    { value: '6', label: 'Sunday' },
  ];

  /** Fetch menu list */
  useEffect(() => {
    dispatch(GetMenuApi(1, 100, '', ''));
  }, [dispatch]);

  useEffect(() => {
  dispatch(GetFoodCategoriesDropdownAPI());
}, [dispatch]);

  /** Populate form fields from API */
  useEffect(() => {
    if (response?.data?.list) {
      const menu = response.data.list.find(item => item._id === id);
      if (menu) {
        setFormData({
          name: menu.name || '',
          description: menu.description || '',
          price: menu.price || '',
          category: menu.category === 0 ? '0' : '1', // 0 for veg, 1 for non-veg
          foodCategory: menu.foodCategoryId || '',
          day: menu.day?.toString() || '0', // Default to Sunday if not set
          images: menu.img ? [menu.img] : []
        });
      }
    }
  }, [response, id]);

  /** Show notifications after save */
  useEffect(() => {
    if (updateResponse?.status) {
      showSuccessToast(updateResponse.message);
      dispatch(GetMenuApi(1, 100, '', ''));
    }
    if (updateError) {
      showErrorToast(updateError.data);
    }
  }, [updateResponse, updateError, dispatch]);

  /** Input change handler */
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
  };

  /** File upload */
  const handleFileChange = e => {
    const files = Array.from(e.target.files);
    if (formData.images.length + newImages.length + files.length > 5) {
      showErrorToast('Maximum upload limit is 5 images.');
      return;
    }

    const valid = [];
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        showErrorToast(`Invalid file type: ${file.name}`);
        continue;
      }
      if (file.size > 2 * 1024 * 1024) {
        showErrorToast(`File ${file.name} exceeds 2MB limit.`);
        continue;
      }
      valid.push({ file, url: URL.createObjectURL(file) });
    }
    setNewImages(prev => [...prev, ...valid].slice(0, 5));
  };

  /** Handle drag and drop */
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileChange({ target: { files } });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  /** Remove image */
  const handleRemoveImage = (index, isNew = false) => {
    if (isNew) {
      setNewImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  /** Validate form */
  const validateForm = () => {
    const errors = {};
    if (!formData.category) errors.category = true;
    if (!formData.foodCategory) errors.foodCategory = true; 
    if (!formData.name.trim()) errors.name = true;
    if (!formData.description.trim()) errors.description = true;
    if (!formData.price) errors.price = true;
    if (!formData.day) errors.day = true;
    if (formData.images.length + newImages.length === 0) errors.images = true;
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** Submit */
  const handleSubmit = () => {
    if (!validateForm()) return;
    const fd = new FormData();
    fd.append('name', formData.name);
    fd.append('description', formData.description);
    fd.append('price', formData.price);
    fd.append('category', formData.category); // Already 0 or 1
    fd.append('foodCategory', formData.foodCategory); 
    fd.append('day', formData.day); // Already 0-6
    newImages.forEach(img => fd.append('images', img.file));
    formData.images.forEach(img => fd.append('existingImages', img));
    dispatch(EditMenuAPI(id, fd));
  };

  /** Reset form on cancel */
  const resetForm = () => {
    if (response?.data?.list) {
      const menu = response.data.list.find(item => item._id === id);
      if (menu) {
        setFormData({
          name: menu.name || '',
          description: menu.description || '',
          price: menu.price || '',
          category: menu.category === 0 ? '0' : '1', // 0 for veg, 1 for non-veg
          foodCategory: menu.foodCategory || '',
          day: menu.day?.toString() || '0', // Default to Sunday if not set
          images: menu.img ? [menu.img] : []
        });
      }
    }
    setNewImages([]);
    setValidationErrors({});
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading menu details</div>;

  return (
    <div className='content_section'>
      <div className='container'>
        <div className='row'>
          <div className='col-lg-10'>
            <h2>Edit Menu</h2>
            <div className='row gy-4'>
              {/* Category */}
              <div className='col-md-4'>
                <div className='input_wrapper'>
                  <label>Food Type</label>
                  <div className='form_field'>
                    <select 
                      name="category"
                      className={`form-select ${validationErrors.category ? 'is-invalid' : ''}`} 
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Category</option>
                      <option value="0">Veg</option>
                      <option value="1">Non-Veg</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Day Selection */}
              <div className='col-md-4'>
                <div className='input_wrapper'>
                  <label>Day</label>
                  <div className='form_field'>
                    <select 
                      name="day"
                      className={`form-select ${validationErrors.day ? 'is-invalid' : ''}`} 
                      value={formData.day}
                      onChange={handleInputChange}
                    >
                      {daysOfWeek.map(day => (
                        <option key={day.value} value={day.value}>{day.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

               <div className='col-md-4'>
                <div className='input_wrapper'>
                  <label>Food Category</label>
                  <div className='form_field'>
                    <select 
                      name="foodCategory"
                      className={`form-select ${validationErrors.foodCategory ? 'is-invalid' : ''}`} 
                      value={formData.foodCategory}
                      onChange={handleInputChange}
                      disabled={foodCategoriesLoading}
                    >
                      <option value="">Select Food Category</option>
                      {foodCategoriesResponse?.data?.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {foodCategoriesLoading && (
                      <div className="mt-2 text-muted">Loading categories...</div>
                    )}
                  </div>
                </div>
              </div>
              
             
              
             
              
              {/* Image Upload Section */}
              <div className='col-md-12'>
                <div className='input_wrapper'>
                  <label>Upload Images</label>
                  <div 
                    className='form_field form_field_upload' 
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <input 
                      type="file" 
                      className='d-none' 
                      id='uploadMenuImg' 
                      multiple 
                      onChange={handleFileChange} 
                      accept="image/jpeg, image/png, image/jpg, image/webp" 
                    />
                    <label htmlFor='uploadMenuImg'>
                      <span><img src='/assets/icons/upload-icon.svg' alt="upload" /></span>
                      <span>Drag or drop here</span>
                      <span>or</span>
                      <span className='primary_text'>Browse</span>
                    </label>
                  </div>
                  <span className="required_info">
                    Maximum 2MB image size and only accepted jpeg, png, jpg, webp. Maximum upload limit is 5.
                  </span>
                  <div className='image-preview-list mt-3 d-flex flex-wrap gap-2'>
                    {formData.images.map((img, index) => (
                      <div key={`existing-${index}`} className='preview-img-box position-relative'>
                        <img
                          src={img}
                          alt={`preview-${index}`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                          }}
                        />
                        <button
                          type='button'
                          onClick={() => handleRemoveImage(index, false)}
                          style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-6px',
                            backgroundColor: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    {newImages.map((img, index) => (
                      <div key={`new-${index}`} className='preview-img-box position-relative'>
                        <img
                          src={img.url}
                          alt={`preview-${index}`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                          }}
                        />
                        <button
                          type='button'
                          onClick={() => handleRemoveImage(index, true)}
                          style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-6px',
                            backgroundColor: '#fff',
                            border: '1px solid #ccc',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                  {validationErrors.images && (
                    <div className="invalid-feedback d-block">
                      Please upload at least one image
                    </div>
                  )}
                </div>
              </div>

               {/* Price */}
              <div className='col-md-6'>
                        <div className='input_wrapper'>
                          <label>Price</label>
                          <div className='form_field'>
                            <input 
                              type='text' 
                              name="price"
                              className={`form-control ${validationErrors.price ? 'is-invalid' : ''}`} 
                              placeholder='Enter price' 
                              value={formData.price}
                              onChange={(e) => {
                                // Only allow numbers and a single decimal point
                                const value = e.target.value;
                                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                  setFormData(prev => ({ ...prev, price: value }));
                                }
                                if (validationErrors.price) {
                                  setValidationErrors(prev => ({ ...prev, price: false }));
                                }
                              }}
                              onKeyDown={(e) => {
                                // Prevent typing alphabets and special characters
                                if (!/[0-9.]/.test(e.key) && 
                                    e.key !== 'Backspace' && 
                                    e.key !== 'Delete' && 
                                    e.key !== 'ArrowLeft' && 
                                    e.key !== 'ArrowRight') {
                                  e.preventDefault();
                                }
                              }}
                              inputMode="numeric"
                            />
                          </div>
                        </div>
                      </div>

               <div className='col-md-6'>
                <div className='input_wrapper'>
                  <label>Food Name</label>
                  <div className='form_field'>
                    <input 
                      type='text' 
                      name="name"
                      className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`} 
                      placeholder='Enter food name' 
                      value={formData.name} 
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              
              {/* Description */}
              <div className='col-md-12'>
                <div className='input_wrapper'>
                  <label>Description</label>
                  <div className='form_field'>
                    <textarea 
                      name="description"
                      className={`form-control ${validationErrors.description ? 'is-invalid' : ''}`} 
                      placeholder='Enter description' 
                      rows={6} 
                      value={formData.description} 
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              
              {/* Save/Cancel Buttons */}
              <div className='col-12'>
                <div className='hstack gap-3 mt-4'>
                  <button className='primary-btn' onClick={handleSubmit} disabled={updateLoading}>
                    {updateLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button className='transparent-btn' onClick={resetForm}>Clear</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuDetails;